import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { casesAPI, clientsAPI, canliiAPI, teamAPI, firmAPI } from '../lib/api';
import { formatDate, getStatusColor, CASE_STATUSES } from '../lib/utils';
import { 
  Briefcase, 
  Plus, 
  Search,
  Calendar,
  Users,
  MoreVertical,
  Edit,
  Eye,
  AlertTriangle,
  Database,
  ExternalLink,
  Loader2,
  CheckCircle,
  XCircle,
  Check,
  ChevronsUpDown,
  UserPlus
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { PageLoader } from '../components/common/LoadingSpinner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Separator } from '../components/ui/separator';
import { Checkbox } from '../components/ui/checkbox';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { toast } from 'sonner';

export const Cases = () => {
  const navigate = useNavigate();
  const [cases, setCases] = useState([]);
  const [clients, setClients] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [practiceAreas, setPracticeAreas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isConflictDialogOpen, setIsConflictDialogOpen] = useState(false);
  const [isCanliiDialogOpen, setIsCanliiDialogOpen] = useState(false);
  const [conflictResult, setConflictResult] = useState(null);
  const [conflictSearchTerm, setConflictSearchTerm] = useState('');
  const [selectedAssignees, setSelectedAssignees] = useState([]);
  const [assigneePopoverOpen, setAssigneePopoverOpen] = useState(false);
  
  // Quick Add Client state
  const [isQuickAddClientOpen, setIsQuickAddClientOpen] = useState(false);
  const [quickClientForm, setQuickClientForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
  });
  const [quickClientSubmitting, setQuickClientSubmitting] = useState(false);
  
  // CanLII lookup state
  const [canliiCitation, setCanliiCitation] = useState('');
  const [canliiLoading, setCanliiLoading] = useState(false);
  const [canliiResult, setCanliiResult] = useState(null);
  const [canliiError, setCanliiError] = useState(null);
  
  const [formData, setFormData] = useState({
    title: '',
    client_id: '',
    practice_area: '',
    status: 'open',
    description: '',
    court_name: '',
    opposing_party: '',
    opposing_counsel: '',
    case_number: '',
    canlii_citation: '',
    canlii_url: '',
    charge_codes: [], // Selected charge codes
  });
  
  // Charge codes state
  const [availableChargeCodes, setAvailableChargeCodes] = useState([]);
  const [chargeCodesLoading, setChargeCodesLoading] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);
  
  // Load charge codes when practice area changes
  useEffect(() => {
    const loadChargeCodes = async () => {
      if (!formData.practice_area) {
        setAvailableChargeCodes([]);
        return;
      }
      
      setChargeCodesLoading(true);
      try {
        const res = await firmAPI.getChargeCodes(formData.practice_area);
        setAvailableChargeCodes(res.data.filter(c => c.enabled) || []);
      } catch (error) {
        console.error('Failed to load charge codes:', error);
        setAvailableChargeCodes([]);
      } finally {
        setChargeCodesLoading(false);
      }
    };
    
    loadChargeCodes();
  }, [formData.practice_area]);

  const fetchData = async () => {
    try {
      const [casesRes, clientsRes, teamRes, practiceAreasRes] = await Promise.all([
        casesAPI.getAll(),
        clientsAPI.getAll(),
        teamAPI.getAll(),
        firmAPI.getEnabledPracticeAreas()
      ]);
      setCases(casesRes.data);
      setClients(clientsRes.data);
      setTeamMembers(teamRes.data || []);
      setPracticeAreas(practiceAreasRes.data || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await casesAPI.create(formData);
      // If assignees selected, assign them to the case
      if (selectedAssignees.length > 0 && response.data?.case_id) {
        await casesAPI.assign(response.data.case_id, selectedAssignees);
      }
      toast.success('Case created successfully');
      fetchData();
      resetForm();
    } catch (error) {
      console.error('Failed to save case:', error);
      toast.error('Failed to create case');
    }
  };

  const handleConflictCheck = async () => {
    if (!conflictSearchTerm.trim()) return;
    try {
      const response = await casesAPI.checkConflict(conflictSearchTerm);
      setConflictResult(response.data);
    } catch (error) {
      console.error('Failed to check conflict:', error);
    }
  };

  // CanLII lookup handler
  const handleCanliiLookup = async () => {
    if (!canliiCitation.trim()) return;
    
    setCanliiLoading(true);
    setCanliiError(null);
    setCanliiResult(null);
    
    try {
      const response = await canliiAPI.lookupCase(canliiCitation);
      setCanliiResult(response.data);
      
      // If found, offer to auto-fill the form
      if (response.data.found && response.data.case) {
        setCanliiResult(response.data);
      }
    } catch (error) {
      console.error('CanLII lookup failed:', error);
      setCanliiError(error.response?.data?.detail || 'Failed to lookup case');
    } finally {
      setCanliiLoading(false);
    }
  };

  // Apply CanLII data to form
  const applyCanLIIData = () => {
    if (!canliiResult?.case) return;
    
    const c = canliiResult.case;
    setFormData(prev => ({
      ...prev,
      title: c.title || prev.title,
      court_name: c.court || prev.court_name,
      case_number: c.citation || prev.case_number,
      canlii_citation: c.citation,
      canlii_url: c.url,
      practice_area: mapKeywordsToPracticeArea(c.keywords) || prev.practice_area,
      description: prev.description || 
        `${c.title}\n\nCourt: ${c.court}\nDecision Date: ${c.decision_date}\nJurisdiction: ${c.jurisdiction}\n\nKeywords: ${(c.keywords || []).join(', ')}\n\nCanLII: ${c.url}`,
    }));
    
    setIsCanliiDialogOpen(false);
    setCanliiResult(null);
    setCanliiCitation('');
  };

  // Map CanLII keywords to our practice areas
  const mapKeywordsToPracticeArea = (keywords) => {
    if (!keywords || !keywords.length) return '';
    
    const keywordLower = keywords.join(' ').toLowerCase();
    
    if (keywordLower.includes('criminal')) return 'Criminal Law';
    if (keywordLower.includes('family') || keywordLower.includes('divorce') || keywordLower.includes('custody')) return 'Family Law';
    if (keywordLower.includes('real estate') || keywordLower.includes('property') || keywordLower.includes('landlord')) return 'Real Estate';
    if (keywordLower.includes('tax')) return 'Tax Law';
    if (keywordLower.includes('immigration')) return 'Immigration';
    if (keywordLower.includes('corporate') || keywordLower.includes('business')) return 'Corporate Law';
    if (keywordLower.includes('employment') || keywordLower.includes('labour')) return 'Employment Law';
    if (keywordLower.includes('intellectual property') || keywordLower.includes('patent') || keywordLower.includes('trademark')) return 'Intellectual Property';
    if (keywordLower.includes('personal injury') || keywordLower.includes('negligence')) return 'Personal Injury';
    if (keywordLower.includes('contract')) return 'Civil Litigation';
    if (keywordLower.includes('estate') || keywordLower.includes('will') || keywordLower.includes('trust')) return 'Estate Planning';
    if (keywordLower.includes('bankrupt') || keywordLower.includes('insolvency')) return 'Bankruptcy';
    if (keywordLower.includes('environment')) return 'Environmental Law';
    
    return '';
  };

  const resetForm = () => {
    setFormData({
      title: '',
      client_id: '',
      practice_area: '',
      status: 'open',
      description: '',
      court_name: '',
      opposing_party: '',
      opposing_counsel: '',
      case_number: '',
      canlii_citation: '',
      canlii_url: '',
      charge_codes: [],
    });
    setSelectedAssignees([]);
    setAvailableChargeCodes([]);
    setIsDialogOpen(false);
  };

  // Quick Add Client
  const handleQuickAddClient = async (e) => {
    e.preventDefault();
    if (!quickClientForm.first_name || !quickClientForm.last_name) {
      toast.error('First name and last name are required');
      return;
    }
    setQuickClientSubmitting(true);
    try {
      const response = await clientsAPI.create({
        first_name: quickClientForm.first_name,
        last_name: quickClientForm.last_name,
        email: quickClientForm.email || undefined,
        phone: quickClientForm.phone || undefined,
        client_type: 'individual',
      });
      // Add the new client to the list
      const newClient = response;
      setClients(prev => [...prev, newClient]);
      // Select the new client in the form
      setFormData(prev => ({ ...prev, client_id: newClient.client_id }));
      toast.success('Client added successfully');
      // Reset and close
      setQuickClientForm({ first_name: '', last_name: '', email: '', phone: '' });
      setIsQuickAddClientOpen(false);
    } catch (error) {
      console.error('Failed to add client:', error);
      toast.error('Failed to add client');
    } finally {
      setQuickClientSubmitting(false);
    }
  };

  const toggleAssignee = (userId) => {
    setSelectedAssignees(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const getAssigneeNames = () => {
    if (selectedAssignees.length === 0) return 'Select team members...';
    const names = selectedAssignees.map(id => {
      const member = teamMembers.find(m => m.user_id === id);
      return member?.name || 'Unknown';
    });
    if (names.length <= 2) return names.join(', ');
    return `${names.slice(0, 2).join(', ')} +${names.length - 2} more`;
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const getClientName = (clientId) => {
    const client = clients.find(c => c.client_id === clientId);
    return client?.name || 'Unknown Client';
  };

  const filteredCases = cases.filter(caseItem => {
    const matchesSearch = caseItem.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      caseItem.case_number?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || caseItem.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return <PageLoader text="Loading cases..." />;
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="cases-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-['Plus_Jakarta_Sans']">Cases</h1>
          <p className="text-slate-500 mt-1">Manage your legal matters</p>
        </div>
        <div className="flex gap-2">
          {/* CanLII Lookup Dialog */}
          <Dialog open={isCanliiDialogOpen} onOpenChange={setIsCanliiDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="gap-2" data-testid="canlii-lookup-btn">
                <Database className="h-4 w-4" />
                CanLII Lookup
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[600px]">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Database className="h-5 w-5 text-blue-600" />
                  Canadian Court Records Lookup
                </DialogTitle>
                <CardDescription>
                  Search CanLII to auto-fill case information from public court records
                </CardDescription>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="flex gap-2">
                  <Input
                    placeholder="Enter citation (e.g., 2022 SCC 30, 2021 ONCA 456)"
                    value={canliiCitation}
                    onChange={(e) => setCanliiCitation(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleCanliiLookup()}
                    data-testid="canlii-citation-input"
                  />
                  <Button 
                    onClick={handleCanliiLookup} 
                    disabled={canliiLoading || !canliiCitation.trim()}
                    data-testid="canlii-search-btn"
                  >
                    {canliiLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                  </Button>
                </div>

                <div className="text-xs text-slate-500">
                  <p className="font-medium mb-1">Supported citation formats:</p>
                  <ul className="list-disc list-inside space-y-0.5">
                    <li>Supreme Court: <span className="font-mono bg-slate-100 px-1 rounded">2022 SCC 30</span></li>
                    <li>Ontario Court of Appeal: <span className="font-mono bg-slate-100 px-1 rounded">2021 ONCA 456</span></li>
                    <li>BC Supreme Court: <span className="font-mono bg-slate-100 px-1 rounded">2023 BCSC 789</span></li>
                    <li>Federal Court of Appeal: <span className="font-mono bg-slate-100 px-1 rounded">2020 FCA 123</span></li>
                  </ul>
                </div>

                {canliiError && (
                  <div className="p-4 bg-red-50 rounded-lg flex items-start gap-3">
                    <XCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
                    <div>
                      <p className="text-sm text-red-700">{canliiError}</p>
                    </div>
                  </div>
                )}

                {canliiResult && (
                  <div className={`p-4 rounded-lg ${canliiResult.found ? 'bg-green-50' : 'bg-yellow-50'}`}>
                    <div className="flex items-start gap-3">
                      {canliiResult.found ? (
                        <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
                      ) : (
                        <AlertTriangle className="h-5 w-5 text-yellow-600 flex-shrink-0" />
                      )}
                      <div className="flex-1">
                        {canliiResult.found ? (
                          <>
                            <p className="font-medium text-green-800 mb-2">Case Found!</p>
                            <div className="space-y-2 text-sm">
                              <p><span className="font-medium">Title:</span> {canliiResult.case.title}</p>
                              <p><span className="font-medium">Citation:</span> {canliiResult.case.citation}</p>
                              <p><span className="font-medium">Court:</span> {canliiResult.case.court}</p>
                              <p><span className="font-medium">Decision Date:</span> {canliiResult.case.decision_date}</p>
                              <p><span className="font-medium">Jurisdiction:</span> {canliiResult.case.jurisdiction}</p>
                              {canliiResult.case.keywords?.length > 0 && (
                                <div className="flex flex-wrap gap-1 mt-2">
                                  {canliiResult.case.keywords.map((kw, idx) => (
                                    <Badge key={idx} variant="secondary" className="text-xs">
                                      {kw}
                                    </Badge>
                                  ))}
                                </div>
                              )}
                              <a 
                                href={canliiResult.case.url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:underline flex items-center gap-1 mt-2"
                              >
                                View on CanLII <ExternalLink className="h-3 w-3" />
                              </a>
                            </div>
                            <Separator className="my-4" />
                            <Button 
                              onClick={applyCanLIIData} 
                              className="w-full gap-2"
                              data-testid="apply-canlii-data-btn"
                            >
                              <CheckCircle className="h-4 w-4" />
                              Apply to New Case Form
                            </Button>
                          </>
                        ) : (
                          <div className="text-yellow-800">
                            <p className="font-medium">Case Not Found</p>
                            <p className="text-sm mt-1">{canliiResult.message}</p>
                          </div>
                        )}

                        {canliiResult.source === 'demo_data' && (
                          <p className="text-xs text-slate-500 mt-3 pt-3 border-t border-slate-200">
                            ℹ️ Demo mode. To access all Canadian court records, request a free API key from{' '}
                            <a href="https://www.canlii.org" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                              CanLII.org
                            </a>
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </DialogContent>
          </Dialog>

          {/* Conflict Check Dialog */}
          <Dialog open={isConflictDialogOpen} onOpenChange={setIsConflictDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="gap-2" data-testid="conflict-check-btn">
                <AlertTriangle className="h-4 w-4" />
                Conflict Check
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Conflict of Interest Check</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="flex gap-2">
                  <Input
                    placeholder="Enter party name..."
                    value={conflictSearchTerm}
                    onChange={(e) => setConflictSearchTerm(e.target.value)}
                    data-testid="conflict-search-input"
                  />
                  <Button onClick={handleConflictCheck} data-testid="run-conflict-check-btn">
                    Check
                  </Button>
                </div>
                
                {conflictResult && (
                  <div className={`p-4 rounded-lg ${conflictResult.has_conflict ? 'bg-red-50' : 'bg-green-50'}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <AlertTriangle className={`h-5 w-5 ${conflictResult.has_conflict ? 'text-red-600' : 'text-green-600'}`} />
                      <span className={`font-medium ${conflictResult.has_conflict ? 'text-red-700' : 'text-green-700'}`}>
                        {conflictResult.has_conflict ? 'Potential Conflict Found' : 'No Conflict Found'}
                      </span>
                    </div>
                    {conflictResult.conflicts?.length > 0 && (
                      <ul className="mt-2 space-y-1 text-sm text-red-700">
                        {conflictResult.conflicts.map((conflict, idx) => (
                          <li key={idx}>
                            • {conflict.type === 'existing_client' ? 'Client' : 'Case'}: {conflict.client_name || conflict.case_title}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </div>
            </DialogContent>
          </Dialog>

          {/* New Case Dialog */}
          <Dialog open={isDialogOpen} onOpenChange={(open) => { if (!open) resetForm(); else setIsDialogOpen(true); }}>
            <DialogTrigger asChild>
              <Button className="gap-2" data-testid="add-case-btn">
                <Plus className="h-4 w-4" />
                New Case
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[600px] max-h-[85vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Create New Case</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                {formData.canlii_citation && (
                  <div className="p-3 bg-blue-50 rounded-lg flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Database className="h-4 w-4 text-blue-600" />
                      <span className="text-sm text-blue-800">
                        Linked to CanLII: <strong>{formData.canlii_citation}</strong>
                      </span>
                    </div>
                    {formData.canlii_url && (
                      <a 
                        href={formData.canlii_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800"
                      >
                        <ExternalLink className="h-4 w-4" />
                      </a>
                    )}
                  </div>
                )}
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2 space-y-2">
                    <Label htmlFor="title">Case Title *</Label>
                    <Input
                      id="title"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      required
                      data-testid="case-title-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="client_id">Client *</Label>
                    <div className="flex gap-2">
                      <Select
                        value={formData.client_id}
                        onValueChange={(value) => setFormData({ ...formData, client_id: value })}
                        required
                      >
                        <SelectTrigger data-testid="case-client-select" className="flex-1">
                          <SelectValue placeholder="Select client" />
                        </SelectTrigger>
                        <SelectContent>
                          {clients.map((client) => (
                            <SelectItem key={client.client_id} value={client.client_id}>
                              {client.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={() => setIsQuickAddClientOpen(true)}
                        title="Add New Client"
                        data-testid="quick-add-client-btn"
                      >
                        <UserPlus className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="practice_area">Practice Area *</Label>
                    <Select
                      value={formData.practice_area}
                      onValueChange={(value) => setFormData({ ...formData, practice_area: value })}
                      required
                    >
                      <SelectTrigger data-testid="case-practice-area-select">
                        <SelectValue placeholder="Select area" />
                      </SelectTrigger>
                      <SelectContent>
                        {practiceAreas.map((area) => (
                          <SelectItem key={area.id} value={area.name}>{area.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Charge Codes - filtered by practice area */}
                  {formData.practice_area && (
                    <div className="col-span-2 space-y-2">
                      <Label>Charge Codes</Label>
                      {chargeCodesLoading ? (
                        <div className="text-sm text-gray-500">Loading charge codes...</div>
                      ) : availableChargeCodes.length === 0 ? (
                        <div className="text-sm text-gray-500">No charge codes available for this practice area</div>
                      ) : (
                        <div className="border rounded-lg p-3 max-h-40 overflow-y-auto space-y-2">
                          {availableChargeCodes.slice(0, 20).map((code) => (
                            <label key={code.code} className="flex items-start gap-2 cursor-pointer hover:bg-gray-50 p-1 rounded">
                              <input
                                type="checkbox"
                                className="mt-1"
                                checked={formData.charge_codes?.includes(code.code)}
                                onChange={(e) => {
                                  const codes = formData.charge_codes || [];
                                  if (e.target.checked) {
                                    setFormData({ ...formData, charge_codes: [...codes, code.code] });
                                  } else {
                                    setFormData({ ...formData, charge_codes: codes.filter(c => c !== code.code) });
                                  }
                                }}
                              />
                              <div>
                                <div className="text-sm font-medium">{code.code}</div>
                                <div className="text-xs text-gray-500">{code.name}</div>
                              </div>
                            </label>
                          ))}
                          {availableChargeCodes.length > 20 && (
                            <div className="text-xs text-gray-500 text-center pt-2">
                              Showing 20 of {availableChargeCodes.length} codes
                            </div>
                          )}
                        </div>
                      )}
                      {formData.charge_codes?.length > 0 && (
                        <div className="text-xs text-gray-600">
                          {formData.charge_codes.length} code(s) selected
                        </div>
                      )}
                    </div>
                  )}
                  
                  <div className="space-y-2">
                    <Label htmlFor="case_number">Case/Citation Number</Label>
                    <Input
                      id="case_number"
                      value={formData.case_number}
                      onChange={(e) => setFormData({ ...formData, case_number: e.target.value })}
                      placeholder="e.g., 2022 SCC 30"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="court_name">Court</Label>
                    <Input
                      id="court_name"
                      value={formData.court_name}
                      onChange={(e) => setFormData({ ...formData, court_name: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="status">Status</Label>
                    <Select
                      value={formData.status}
                      onValueChange={(value) => setFormData({ ...formData, status: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CASE_STATUSES.map((status) => (
                          <SelectItem key={status.value} value={status.value}>{status.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="opposing_party">Opposing Party</Label>
                    <Input
                      id="opposing_party"
                      value={formData.opposing_party}
                      onChange={(e) => setFormData({ ...formData, opposing_party: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="opposing_counsel">Opposing Legal Representative</Label>
                    <Input
                      id="opposing_counsel"
                      value={formData.opposing_counsel}
                      onChange={(e) => setFormData({ ...formData, opposing_counsel: e.target.value })}
                    />
                  </div>
                  <div className="col-span-2 space-y-2">
                    <Label htmlFor="description">Description</Label>
                    <Textarea
                      id="description"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      rows={4}
                    />
                  </div>
                  
                  {/* Team Member Assignment */}
                  <div className="col-span-2 space-y-2">
                    <Label>Assign Team Members</Label>
                    <Popover open={assigneePopoverOpen} onOpenChange={setAssigneePopoverOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className="w-full justify-between font-normal"
                          data-testid="case-assignees-btn"
                        >
                          <span className={selectedAssignees.length === 0 ? 'text-slate-500' : ''}>
                            {getAssigneeNames()}
                          </span>
                          <ChevronsUpDown className="h-4 w-4 text-slate-400" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[300px] p-0" align="start">
                        <div className="p-2 border-b">
                          <p className="text-sm font-medium text-slate-700">Select team members</p>
                        </div>
                        <div className="max-h-[200px] overflow-y-auto p-2 space-y-1">
                          {teamMembers.length > 0 ? (
                            teamMembers.map((member) => (
                              <div
                                key={member.user_id}
                                className="flex items-center gap-3 p-2 rounded-md hover:bg-slate-50 cursor-pointer"
                                onClick={() => toggleAssignee(member.user_id)}
                                data-testid={`assignee-${member.user_id}`}
                              >
                                <Checkbox 
                                  checked={selectedAssignees.includes(member.user_id)}
                                  onCheckedChange={() => toggleAssignee(member.user_id)}
                                />
                                <Avatar className="h-7 w-7">
                                  <AvatarImage src={member.picture} />
                                  <AvatarFallback className="text-xs bg-blue-100 text-blue-700">
                                    {getInitials(member.name)}
                                  </AvatarFallback>
                                </Avatar>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium truncate">{member.name}</p>
                                  <p className="text-xs text-slate-500 capitalize">{member.role}</p>
                                </div>
                              </div>
                            ))
                          ) : (
                            <p className="text-sm text-slate-500 text-center py-4">
                              No team members found
                            </p>
                          )}
                        </div>
                        {selectedAssignees.length > 0 && (
                          <div className="p-2 border-t">
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="w-full text-xs"
                              onClick={() => setSelectedAssignees([])}
                            >
                              Clear selection
                            </Button>
                          </div>
                        )}
                      </PopoverContent>
                    </Popover>
                    {selectedAssignees.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {selectedAssignees.map(id => {
                          const member = teamMembers.find(m => m.user_id === id);
                          return (
                            <Badge key={id} variant="secondary" className="gap-1">
                              {member?.name || 'Unknown'}
                              <button
                                type="button"
                                className="ml-1 hover:text-red-600"
                                onClick={() => toggleAssignee(id)}
                              >
                                ×
                              </button>
                            </Badge>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={resetForm}>Cancel</Button>
                  <Button type="submit" data-testid="save-case-btn">Create Case</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Search cases..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="search-cases-input"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px]" data-testid="status-filter">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            {CASE_STATUSES.map((status) => (
              <SelectItem key={status.value} value={status.value}>{status.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Cases Table */}
      {filteredCases.length > 0 ? (
        <Card data-testid="cases-table">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Case</th>
                    <th>Client</th>
                    <th>Practice Area</th>
                    <th>Status</th>
                    <th>Created</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCases.map((caseItem) => (
                    <tr key={caseItem.case_id} data-testid={`case-row-${caseItem.case_id}`}>
                      <td>
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-slate-900">{caseItem.title}</p>
                            {caseItem.canlii_citation && (
                              <Badge variant="outline" className="text-xs bg-blue-50">
                                <Database className="h-3 w-3 mr-1" />
                                CanLII
                              </Badge>
                            )}
                          </div>
                          <p className="text-xs text-slate-500">{caseItem.case_number}</p>
                        </div>
                      </td>
                      <td>
                        <div className="flex items-center gap-2">
                          <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center">
                            <Users className="h-4 w-4 text-blue-600" />
                          </div>
                          <span>{getClientName(caseItem.client_id)}</span>
                        </div>
                      </td>
                      <td>
                        <Badge variant="outline">{caseItem.practice_area}</Badge>
                      </td>
                      <td>
                        <Badge className={getStatusColor(caseItem.status)}>
                          {caseItem.status}
                        </Badge>
                      </td>
                      <td className="text-slate-500">
                        {formatDate(caseItem.created_at)}
                      </td>
                      <td>
                        <div className="flex items-center gap-2">
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="h-8 gap-1"
                            onClick={() => navigate(`/case/${caseItem.case_id}`)}
                            data-testid={`view-case-${caseItem.case_id}`}
                          >
                            <Eye className="h-3.5 w-3.5" />
                            View
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="h-8 gap-1"
                            onClick={() => navigate(`/case/${caseItem.case_id}`)}
                            data-testid={`edit-case-${caseItem.case_id}`}
                          >
                            <Edit className="h-3.5 w-3.5" />
                            Edit
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-12 text-center">
            <Briefcase className="h-12 w-12 mx-auto text-slate-300 mb-4" />
            <h3 className="text-lg font-medium text-slate-900 mb-2">No cases found</h3>
            <p className="text-slate-500 mb-4">
              {searchTerm ? 'Try a different search term' : 'Get started by creating your first case'}
            </p>
            {!searchTerm && (
              <div className="flex justify-center gap-3">
                <Button variant="outline" onClick={() => setIsCanliiDialogOpen(true)} className="gap-2">
                  <Database className="h-4 w-4" />
                  Lookup from CanLII
                </Button>
                <Button onClick={() => setIsDialogOpen(true)} className="gap-2" tooltip="Create a new legal case">
                  <Plus className="h-4 w-4" />
                  New Case
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Quick Add Client Dialog */}
      <Dialog open={isQuickAddClientOpen} onOpenChange={setIsQuickAddClientOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="h-5 w-5" />
              Quick Add Client
            </DialogTitle>
            <DialogDescription>
              Add a new client to associate with this case.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleQuickAddClient} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="quick_first_name">First Name *</Label>
                <Input
                  id="quick_first_name"
                  value={quickClientForm.first_name}
                  onChange={(e) => setQuickClientForm({ ...quickClientForm, first_name: e.target.value })}
                  required
                  placeholder="John"
                  data-testid="quick-client-first-name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="quick_last_name">Last Name *</Label>
                <Input
                  id="quick_last_name"
                  value={quickClientForm.last_name}
                  onChange={(e) => setQuickClientForm({ ...quickClientForm, last_name: e.target.value })}
                  required
                  placeholder="Doe"
                  data-testid="quick-client-last-name"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="quick_email">Email</Label>
              <Input
                id="quick_email"
                type="email"
                value={quickClientForm.email}
                onChange={(e) => setQuickClientForm({ ...quickClientForm, email: e.target.value })}
                placeholder="john@example.com"
                data-testid="quick-client-email"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="quick_phone">Phone</Label>
              <Input
                id="quick_phone"
                type="tel"
                value={quickClientForm.phone}
                onChange={(e) => setQuickClientForm({ ...quickClientForm, phone: e.target.value })}
                placeholder="+1 (555) 123-4567"
                data-testid="quick-client-phone"
              />
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setIsQuickAddClientOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={quickClientSubmitting} className="gap-2" data-testid="quick-client-save-btn">
                {quickClientSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />}
                Add Client
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Cases;
